import type { BlockingAccessorialSchedule } from "../../hooks/react-query/backOffice/useAccessorialCharges";

export const mockAccessorialChargesListData = {
  accessorialCharges: [
    {
      id: 1,
      code: "BlindFee",
      description: "Blind Shipment Fee",
      rateConfiguration: "Flat Rate",
      rateMinMax: "$1,699",
      appliesTo: "Pickup",
      autoAssign: "No",
      status: "Active",
    },
    {
      id: 2,
      code: "BlindFee2",
      description: "Blind Shipment Fee",
      rateConfiguration: "Multiple Rates",
      rateMinMax: "Multiple Rates",
      appliesTo: "Pickup",
      autoAssign: "No",
      status: "Draft",
    },
    {
      id: 3,
      code: "BlindFee3",
      description: "Blind Shipment Fee",
      rateConfiguration: "Rate per Unit",
      rateMinMax: "$1,699 ($3,699, $1,699)",
      appliesTo: "Delivery",
      autoAssign: "Yes",
      status: "Active",
    },
    {
      id: 4,
      code: "BlindFee4",
      description: "Blind Shipment Fee",
      rateConfiguration: "Percentage",
      rateMinMax: "15% (3.49%, $5,699)",
      appliesTo: "Both",
      autoAssign: "Yes",
      status: "Active",
    },
    {
      id: 5,
      code: "BlindFee5",
      description: "Blind Shipment Fee",
      rateConfiguration: "Flat Rate",
      rateMinMax: "$1,699",
      appliesTo: "Both",
      autoAssign: "No",
      status: "Active",
    },
    {
      id: 6,
      code: "BlindFee6",
      description: "Blind Shipment Fee",
      rateConfiguration: "Detention",
      rateMinMax: "-",
      appliesTo: "Pickup",
      autoAssign: "Yes",
      status: "Draft",
    },
    {
      id: 7,
      code: "LiftGateFee",
      description: "Lift Gate Service Fee",
      rateConfiguration: "Flat Rate",
      rateMinMax: "$75.00",
      appliesTo: "Delivery",
      autoAssign: "Yes",
      status: "Active",
    },
    {
      id: 8,
      code: "ResidentialFee",
      description: "Residential Delivery Fee",
      rateConfiguration: "Flat Rate",
      rateMinMax: "$150.00",
      appliesTo: "Delivery",
      autoAssign: "Yes",
      status: "Active",
    },
    {
      id: 9,
      code: "InsideFee",
      description: "Inside Delivery Fee",
      rateConfiguration: "Rate per Unit",
      rateMinMax: "$50.00 ($25.00, $500.00)",
      appliesTo: "Delivery",
      autoAssign: "No",
      status: "Active",
    },
    {
      id: 10,
      code: "AppointmentFee",
      description: "Appointment Delivery Fee",
      rateConfiguration: "Flat Rate",
      rateMinMax: "$100.00",
      appliesTo: "Both",
      autoAssign: "Yes",
      status: "Active",
    },
  ],
};

// Rating Configuration Types and Mock Data

export interface RateSequenceTableRow {
  minWeight: string;
  maxWeight: string;
  freeTime: string;
}

/**
 * Mock criteria data for accessorial charges
 * Matches the API response structure for accessorial criteria
 */
export const mockAccessorialCriteria = {
  groups: [
    {
      sequences: [
        {
          startTableName: "SHIPMENT",
          endTableName: "ZONE",
          startFieldName: "COLLECT_BILL",
          endFieldName: "EXPORT_NAME",
          startLookup: "SHIPPER",
          endLookup: "ZONE",
          startLogic: "!=",
          endLogic: ">=",
          logicValue: "not like",
          startMin: "20",
          startMax: "30",
          endMin: "30",
          endMax: "40",
          reverseRange: true,
        },
      ],
    },
    {
      sequences: [
        {
          startTableName: "SHIPMENT",
          endTableName: "ZONE",
          startFieldName: "COLLECT_BILL",
          endFieldName: "EXPORT_NAME",
          startLookup: "SHIPPER",
          endLookup: "ZONE",
          startLogic: "!=",
          endLogic: ">=",
          logicValue: "not like",
          startMin: "20",
          startMax: "30",
          endMin: "30",
          endMax: "40",
          reverseRange: true,
        },
      ],
    },
  ],
  betweenGroupLogic: ["AND"],
};

/** Schedules shown in the inactivate-accessorial-charge warning dialog (mock until API exists). */
export const mockInactivateWarningSchedules: BlockingAccessorialSchedule[] = [
  {
    id: 101,
    name: "Blind Shipment Fee",
    description: "Blind Shipment Fee",
    status: "Active",
  },
  {
    id: 102,
    name: "Lift Gate Service Schedule",
    description: "Lift Gate Service Schedule",
    status: "Active",
  },
  {
    id: 103,
    name: "Residential Delivery Schedule",
    description: "Residential Delivery Schedule",
    status: "Active",
  },
];

// Mock detailed accessorial charge for getById API
export const mockAccessorialChargeDetail = {
  id: 190,
  code: "ACC-ALL-RATE-TYPES-1102",
  shortDescription: "All rate types with criteria",
  longDescription:
    "Accessorial create with description-based table/field names",
  serviceLevel: [
    {
      id: 1,
      description: "POOL",
    },
  ],
  appliesTo: "Both",
  accessorialType: "Liftgate",
  currencyType: "USD",
  shippingInstructions: [
    {
      id: 4,
      description: "Inside Pickup",
    },
  ],
  interlinerCharges: false,
  autoAssign: true,
  status: "Active",
  createdBy: "a2c69fcc-0982-44e2-9223-81b425d0ddae",
  createdOn: "04/07/2026",
  modifiedBy: "a2c69fcc-0982-44e2-9223-81b425d0ddae",
  modifiedOn: "04/07/2026",
  rateSequences: [
    {
      rateType: "Flat Rate",
      rate: "25.50",
      criteria: {
        criteriaId: 1001275,
        numberOfGroups: 2,
        numberOfSequences: 4,
      },
    },
    {
      rateType: "Rate per Unit",
      unit: "Lbs",
      ratePerUnit: "1.75",
      minimumCharge: "10.00",
      maximumCharge: "200.00",
      criteria: {
        criteriaId: 1001276,
        numberOfGroups: 2,
        numberOfSequences: 4,
      },
    },
    {
      rateType: "Percentage",
      percentageRate: "12.50",
      calculatePercentageOf: "Weight",
      minimumCharge: "5.00",
      maximumCharge: "150.00",
      criteria: {
        criteriaId: 1001277,
        numberOfGroups: 2,
        numberOfSequences: 4,
      },
    },
    {
      rateType: "Detention",
      overtimeRatePerIncrement: "15.00",
      incrementDurationMins: 30,
      minimumChargePickup: "20.00",
      minimumChargeDelivery: "25.00",
      multiShipment: {
        proportionalBillingEnabled: true,
        applyMinimumPerShipment: false,
      },
      weightTiers: [
        {
          minimumWeightLbs: 0,
          maximumWeightLbs: 500,
          freeTimeMins: 30,
        },
        {
          minimumWeightLbs: 501,
          maximumWeightLbs: 2000,
          freeTimeMins: 45,
        },
      ],
      criteria: {
        criteriaId: 1001278,
        numberOfGroups: 2,
        numberOfSequences: 4,
      },
    },
  ],
};
